import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppContext } from "../../context/AppContext";
import { useBrand } from "../../context/BrandContext";
import { useCopy } from "../../context/CopyContext";
import { useIrisControl } from "../../hooks/useIrisControl";
import Header from "../Header";
import Heading from "../Heading";
import Button from "../Button";
import MultiLineCopy from "../MultiLineCopy";
import PoweredBy from "../PoweredBy";
import ItemsModalView from "./ItemsModalView";

const PlaceEmptyView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, dispatch } = useAppContext();
  const { brandConfig, isDarkMode } = useBrand();
  const { getCopy } = useCopy();
  const { openIris } = useIrisControl();
  const [showItemsModal, setShowItemsModal] = useState(false);
  const hasAddedItemRef = useRef(false);

  // Check if this is a back navigation from email capture
  const isBackFromEmail = location.state?.fromEmailCapture === true;

  // Check if we're coming from EmptyAcceptedView
  const fromEmptyAccepted = location.state?.fromEmptyAccepted === true;

  // Use global dispensed state instead of local state
  const isDispensed = state.kiosk.isItemDispensed || isBackFromEmail;

  const handleCancel = () => {
    navigate("/empty-accepted");
  };

  const handleAddAnother = () => {
    dispatch({ type: 'SET_ITEM_DISPENSED', payload: false });
    navigate("/add-empty");
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  // Open iris and add item when coming from EmptyAcceptedView
  useEffect(() => {
    if (fromEmptyAccepted && !isBackFromEmail && !hasAddedItemRef.current) {
      // Mark that we're processing this item
      hasAddedItemRef.current = true;

      // Reset dispensed state
      dispatch({ type: 'SET_ITEM_DISPENSED', payload: false });

      // Open Iris for 3 seconds to allow item return
      console.log('🔓 Opening Iris for item return (single-item flow)');
      openIris(3);

      // Add the previewed product to cart (only once)
      if (state.lastAddedProduct) {
        dispatch({ type: 'ADD_EMPTY', payload: state.lastAddedProduct });
        console.log('✅ Added product to cart:', state.lastAddedProduct.name);
      }

      // Mark as dispensed after iris opens
      dispatch({ type: 'SET_ITEM_DISPENSED', payload: true });
    }
  }, [fromEmptyAccepted, isBackFromEmail, openIris, dispatch, state.lastAddedProduct]);

  // Reset the ref when navigating away
  useEffect(() => {
    return () => {
      hasAddedItemRef.current = false;
    };
  }, []);

  // Reset dispensed state when starting fresh
  useEffect(() => {
    if (!isBackFromEmail && !fromEmptyAccepted && !state.kiosk.isItemDispensed) {
      dispatch({ type: 'SET_ITEM_DISPENSED', payload: false });
    }
  }, [dispatch, isBackFromEmail, fromEmptyAccepted, state.kiosk.isItemDispensed]);

  return (
    <div
      className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden"
      data-node-id="75:1001"
    >
      {/* Animated Teal Glow Effect at Bottom - show for both states */}
      <motion.div
        className="absolute bottom-[-675px] left-0  w-[1080px] h-[1080px] rounded-[1000px] pointer-events-none"
        style={{
          background: brandConfig.colors.primary[isDarkMode ? 'dark' : 'light'],
          filter: 'blur(200px)',
        }}
        animate={{
          y: [0, -100, 0],
          opacity: [.25, .75, .25],
          scale: [1, 1.25, 1],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      {/* Content Container */}
      <div className="relative z-10 flex-1 flex flex-col gap-24">
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              onItemsClick={handleItemsClick}
            />
          </div>
        </div>

        {/* Main Content Area - with 3-row grid */}
        <motion.div
          className="flex-1 grid grid-rows-3 gap-0 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Rows 1 & 2 - Main heading spanning two rows */}
          <div className="row-span-2 flex items-center justify-center">
            <div className="text-center w-full max-w-[920px]">
              <AnimatePresence mode="wait">
                {!isDispensed ? (
                  <motion.div
                    key="place-empty"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{
                      duration: 0.6,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  >
                    <Heading
                      title={getCopy('placeEmpty.heading')}
                      subtitle={getCopy('placeEmpty.subheading')}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="thank-you"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{
                      duration: 0.6,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  >
                    <Heading
                      title={getCopy('placeEmpty.thankYouHeading')}
                      subtitle={getCopy('placeEmpty.thankYouSubheading')}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Row 3 - Instruction text */}
          <div className="flex items-end justify-center">
            <div className="text-center w-full max-w-[680px]">
              <AnimatePresence mode="wait">
                {!isDispensed ? (
                  <motion.div
                    key="listen-sound"
                    className="font-brand-secondary font-brand-light leading-none not-italic text-[32px] text-center text-brand-text-primary"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{
                      duration: 0.5,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  >
                    <MultiLineCopy 
                      text={getCopy('placeEmpty.helpText')}
                      className="leading-[42px]"
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="ready-more"
                    className="font-brand-secondary font-brand-light leading-none not-italic text-[32px] text-center text-brand-text-primary"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{
                      duration: 0.5,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  >
                    <MultiLineCopy 
                      text={getCopy('placeEmpty.thankYouHelpText')}
                      className="leading-[42px]"
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>

        {/* Bottom Section - with padding */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Conditional Buttons */}
          <AnimatePresence mode="wait">
            {!isDispensed ? (
              <motion.div 
                key="cancel-button"
                className="flex justify-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ 
                  duration: 0.5, 
                  ease: [0.25, 0.1, 0.25, 1] 
                }}
              >
                <Button variant="secondary" onClick={handleCancel}>
                  {getCopy('placeEmpty.buttons.secondary')}
                </Button>
              </motion.div>
            ) : (
              <motion.div 
                key="action-buttons"
                className="flex flex-row gap-6 lg:gap-10 w-full"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ 
                  duration: 0.6, 
                  ease: [0.25, 0.1, 0.25, 1] 
                }}
              >
                <Button variant="secondary" onClick={handleAddAnother}>
                  {getCopy('placeEmpty.buttons.addAnother')}
                </Button>
                <Button 
                  variant="primary" 
                  onClick={() => {
                    dispatch({ type: "SET_STEP", payload: 6 });
                    navigate("/email-capture");
                  }}
                >
                  {getCopy('placeEmpty.buttons.continue')}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={handleCloseItemsModal}
      />
    </div>
  );
};

export default PlaceEmptyView;
